/**
 * Game-feel tunables in one place: hit feedback, screen shake, the camera's
 * follow, spawn and death squash, the movement lean. Everything here is
 * presentation only; the simulation's timings and damage never read it.
 */

export const FEEL = {
  hit: {
    /** Body whitening on a hit, and the longer, brighter one on a crit. */
    flashS: 0.09,
    critFlashS: 0.14,
    flashStrength: 0.85,
    critFlashStrength: 1,
    /** Screen-px shove away from the hit point, decaying over `nudgeS`; crits push harder. */
    nudgePx: 3,
    critNudgePx: 5,
    nudgeS: 0.14,
    /** The drawn frame holds this long on a kill of your own (longer for the boss), at most once per `stopGapS`. */
    stopS: 0.045,
    bossStopS: 0.09,
    ownDeathStopS: 0.08,
    stopGapS: 0.25,
  },
  shake: {
    /** Hard cap on the amplitude in CSS px. */
    maxPx: 10,
    /** Explosions: px per unit of blast radius. */
    explosionPerUnit: 2,
    beamcross: 8,
    bolt: 7,
    nova: 3,
    quake: 5,
    wall: 2,
    fissure: 3,
    conflagration: 6,
    phoenix: 4,
    /** A hit of 200+ on yourself. */
    bigHit: 3,
    ownDeath: 6,
    ownShock: 4,
    bossDeath: 10,
    /** The jotunn's swing: the ground answers it. */
    bossStomp: 5,
    bossStompS: 0.3,
  },
  camera: {
    /** Follow smoothing rate (1/s); higher snaps faster. */
    followRate: 7,
    /** The cursor lookahead eases at this rate (1/s), so a flick of the mouse does not yank the view. */
    lookaheadRate: 4,
  },
  /** Enemies pop out of the gate wide and low, settling over this long. */
  spawnS: 0.28,
  /** A dying body squashes flat and fades over this long while its chunks fly. */
  deathS: 0.22,
  lean: {
    /** Radians a wizard tilts into sideways movement, eased at `rate` (1/s). */
    rad: 0.07,
    rate: 12,
  },
} as const;

export interface Pose { sx: number; sy: number; alpha: number }

/** Spawn squash for a body `age` seconds old; false once it has settled (draw it plain). */
export function spawnPose(age: number, out: Pose): boolean {
  if (age < 0 || age >= FEEL.spawnS) return false;
  const k = age / FEEL.spawnS;
  const ease = 1 - (1 - k) * (1 - k) * (1 - k);
  out.sx = 1 + 0.35 * (1 - ease);
  out.sy = 0.55 + 0.45 * ease;
  out.alpha = 1;
  return true;
}

/** Death squash `f` (0..1) of the way through: flattens, widens and fades. */
export function deathPose(f: number, out: Pose): void {
  const c = f < 0 ? 0 : f > 1 ? 1 : f;
  const k = 1 - (1 - c) * (1 - c);
  out.sx = 1 + 0.6 * k;
  out.sy = Math.max(0.05, 1 - 0.95 * k);
  out.alpha = 1 - c;
}

/** Exponential ease toward a target: the fraction covered in `dt` at `rate` per second. */
export function easeK(rate: number, dt: number): number {
  return 1 - Math.exp(-rate * Math.max(0, dt));
}
